import { Injectable } from '@angular/core';
import {
  requestLoading,
  RequestLoadingType,
} from '@ieCore/helpers/request-loading.helper';
import { PaginatedResponseDto } from 'shared-general-libs/dto/pagination';
import { MaterialityRepository } from '@ieCore/repositories/materiality.repository';
import { ContactsRequestInterface } from '@ieShared/interfaces/contacts.interface';
import {
  ContactResponseDto,
  CreateAnswerDto,
  CreateContactDto,
  CreateSurveyDto,
  GetFormDataResponseDto,
  ManageSurveyParticipantsDto,
  SurveyResponseDto,
  SurveyResponseWithStatsDto,
  UpdateSurveyDto,
  UpdateSurveyEndDateDto,
} from 'shared-general-libs/dto/materiality';
import {
  ParticipantsPaginationInterface,
  SurveyRequestInterface,
} from '@ieShared/interfaces/materiality.interface';
import { ImportanceType } from 'shared-general-libs/type/materiality';
import { ProjectRequestInterface } from '@ieShared/interfaces/project.interface';
import {
  AutoAllocateDto,
  ConfirmGoalDto,
  CreateBaselineDto,
  CreateGoalDto,
  CreateProjectActivityDto,
  CreateProjectDto,
  CreateProjectExpenseDto,
  CreateProjectMilestoneDto,
  CustomAllocationDto,
  FilterBaselinesDto,
  FilterGoalsDto,
  UpdateBaselineDto,
  UpdateGoalDto,
  UpdateProjectActivityDto,
  UpdateProjectDto,
  UpdateProjectExpenseDto,
  UpdateProjectMilestoneDto,
} from 'shared-general-libs';
import { CreateProjectTeamDto } from 'shared-general-libs/dto/project';
import { CreatePlanDto, UpdatePlanDto } from 'shared-general-libs/dto/plan';

@Injectable({
  providedIn: 'root',
})
export class MaterialityService {
  constructor(private readonly materialityRepo: MaterialityRepository) {}

  getContacts(
    pagination: ContactsRequestInterface,
  ): RequestLoadingType<PaginatedResponseDto<ContactResponseDto>> {
    return requestLoading(this.materialityRepo.getContacts(pagination));
  }

  getContactById(id: string): RequestLoadingType<ContactResponseDto> {
    return requestLoading(this.materialityRepo.getContactById(id));
  }

  createContact(
    payload: CreateContactDto,
  ): RequestLoadingType<ContactResponseDto> {
    return requestLoading(this.materialityRepo.createContact(payload));
  }

  editContact(
    payload: CreateContactDto,
    id: string,
  ): RequestLoadingType<ContactResponseDto> {
    return requestLoading(this.materialityRepo.editContact(payload, id));
  }

  deleteContact(id: string): RequestLoadingType<void> {
    return requestLoading(this.materialityRepo.deleteContact(id));
  }

  getSurveys(
    pagination: SurveyRequestInterface,
  ): RequestLoadingType<PaginatedResponseDto<SurveyResponseWithStatsDto>> {
    return requestLoading(this.materialityRepo.getSurveys(pagination));
  }

  getSurveyById(id: string): RequestLoadingType<SurveyResponseDto> {
    return requestLoading(this.materialityRepo.getSurveyById(id));
  }

  getSurveyStatsById(id: string): RequestLoadingType<any> {
    return requestLoading(this.materialityRepo.getSurveyStatsById(id));
  }

  createSurvey(payload: CreateSurveyDto): RequestLoadingType<void> {
    return requestLoading(this.materialityRepo.createSurvey(payload));
  }

  addStakeholders(
    id: string,
    payload: ManageSurveyParticipantsDto,
  ): RequestLoadingType<void> {
    return requestLoading(this.materialityRepo.addStakeholders(id, payload));
  }

  updateSurvey(id: string, payload: UpdateSurveyDto): RequestLoadingType<void> {
    return requestLoading(this.materialityRepo.updateSurvey(id, payload));
  }

  getSurveyParticipants(
    id: string,
  ): RequestLoadingType<GetFormDataResponseDto> {
    return requestLoading(this.materialityRepo.getSurveyParticipants(id));
  }

  getSurveyParticipantsList(
    id: string,
    pagination: ParticipantsPaginationInterface,
  ): RequestLoadingType<PaginatedResponseDto<ContactResponseDto>> {
    return requestLoading(
      this.materialityRepo.getSurveyParticipantsList(id, pagination),
    );
  }

  getAggregatedStats(id: string): RequestLoadingType<any> {
    return requestLoading(this.materialityRepo.getAggregatedStats(id));
  }

  getAggregatedByContacts(
    id: string,
    topicId: string,
    pagination: any,
  ): RequestLoadingType<any> {
    return requestLoading(
      this.materialityRepo.getAggregatedByContacts(id, topicId, pagination),
    );
  }

  saveSurveyAnswer(
    id: string,
    payload: CreateAnswerDto,
  ): RequestLoadingType<void> {
    return requestLoading(this.materialityRepo.saveSurveyAnswer(id, payload));
  }

  finalize(id: string, topics: string[]): RequestLoadingType<void> {
    return requestLoading(this.materialityRepo.finalize(id, topics));
  }

  sendSurvey(
    id: string,
    importanceType: ImportanceType,
  ): RequestLoadingType<void> {
    return requestLoading(this.materialityRepo.sendSurvey(id, importanceType));
  }

  updateSurveyReminderDay(
    id: string,
    payload: UpdateSurveyEndDateDto,
  ): RequestLoadingType<void> {
    return requestLoading(
      this.materialityRepo.updateSurveyReminderDay(id, payload),
    );
  }

  getContactsAnswer(
    surveyId: string,
    contactIds: string[],
  ): RequestLoadingType<any> {
    return requestLoading(
      this.materialityRepo.getContactsAnswer(surveyId, contactIds),
    );
  }

  getProjectById(id: string) {
    return requestLoading(this.materialityRepo.getProjectById(id));
  }

  getProjects(pagination: ProjectRequestInterface) {
    return requestLoading(this.materialityRepo.getProjects(pagination));
  }

  createProject(payload: CreateProjectDto) {
    return requestLoading(this.materialityRepo.createProject(payload));
  }

  updateProject(id: string, payload: UpdateProjectDto) {
    return requestLoading(this.materialityRepo.updateProject(id, payload));
  }

  updateProjectStatus(id: string, payload: UpdateProjectDto) {
    return requestLoading(
      this.materialityRepo.updateProjectStatus(id, payload),
    );
  }

  getProjectMilestones(projectId: string) {
    return requestLoading(this.materialityRepo.getProjectMilestones(projectId));
  }

  getProjectMilestoneById(id: string) {
    return requestLoading(this.materialityRepo.getProjectMilestoneById(id));
  }

  createProjectMilestone(payload: CreateProjectMilestoneDto) {
    return requestLoading(this.materialityRepo.createProjectMilestone(payload));
  }

  updateProjectMilestone(id: string, payload: UpdateProjectMilestoneDto) {
    return requestLoading(
      this.materialityRepo.updateProjectMilestone(id, payload),
    );
  }

  deleteProjectMilestone(id: string) {
    return requestLoading(this.materialityRepo.deleteProjectMilestone(id));
  }

  getProjectActivities(projectId: string) {
    return requestLoading(this.materialityRepo.getProjectActivities(projectId));
  }

  getProjectActivityById(id: string) {
    return requestLoading(this.materialityRepo.getProjectActivityById(id));
  }

  createProjectActivity(payload: CreateProjectActivityDto) {
    return requestLoading(this.materialityRepo.createProjectActivity(payload));
  }

  updateProjectActivity(id: string, payload: UpdateProjectActivityDto) {
    return requestLoading(
      this.materialityRepo.updateProjectActivity(id, payload),
    );
  }

  deleteProjectActivity(id: string) {
    return requestLoading(this.materialityRepo.deleteProjectActivity(id));
  }

  getProjectExpenses(projectId: string) {
    return requestLoading(this.materialityRepo.getProjectExpenses(projectId));
  }

  getProjectExpenseById(id: string) {
    return requestLoading(this.materialityRepo.getProjectExpenseById(id));
  }

  createProjectExpense(payload: CreateProjectExpenseDto) {
    return requestLoading(this.materialityRepo.createProjectExpense(payload));
  }

  updateProjectExpense(id: string, payload: UpdateProjectExpenseDto) {
    return requestLoading(
      this.materialityRepo.updateProjectExpense(id, payload),
    );
  }

  deleteProjectExpense(id: string) {
    return requestLoading(this.materialityRepo.deleteProjectExpense(id));
  }

  getTeams(projectId: string) {
    return requestLoading(this.materialityRepo.getTeams(projectId));
  }

  getTeamParticipants(projectId: string) {
    return requestLoading(this.materialityRepo.getTeamParticipants(projectId));
  }

  getTeamById(id: string) {
    return requestLoading(this.materialityRepo.getTeamById(id));
  }

  createTeam(payload: CreateProjectTeamDto) {
    return requestLoading(this.materialityRepo.createTeam(payload));
  }

  getPlans(queryParams: any) {
    return requestLoading(this.materialityRepo.getPlans(queryParams));
  }

  getPlanById(id: string) {
    return requestLoading(this.materialityRepo.getPlanById(id));
  }

  editPlan(id: string, payload: UpdatePlanDto) {
    return requestLoading(this.materialityRepo.editPlan(id, payload));
  }

  createPlan(payload: CreatePlanDto) {
    return requestLoading(this.materialityRepo.createPlan(payload));
  }

  deletePlan(id: string) {
    return requestLoading(this.materialityRepo.deletePlan(id));
  }

  getBaselineStats() {
    return requestLoading(this.materialityRepo.getBaselineStats());
  }

  getBaselineStatsByTopic(topicKey: string) {
    return requestLoading(
      this.materialityRepo.getBaselineStatsByTopic(topicKey),
    );
  }

  getBaselineList(queryParams: FilterBaselinesDto) {
    return requestLoading(this.materialityRepo.getBaselineList(queryParams));
  }

  getBaselineById(id: string) {
    return requestLoading(this.materialityRepo.getBaselineById(id));
  }

  createBaseline(payload: CreateBaselineDto) {
    return requestLoading(this.materialityRepo.createBaseline(payload));
  }

  updateBaseline(id: string, payload: UpdateBaselineDto) {
    return requestLoading(this.materialityRepo.updateBaseline(id, payload));
  }

  completeBaseline(id: string) {
    return requestLoading(this.materialityRepo.completeBaseline(id));
  }

  makeInProgressBaseline(id: string) {
    return requestLoading(this.materialityRepo.makeInProgressBaseline(id));
  }

  deleteBaseline(id: string) {
    return requestLoading(this.materialityRepo.deleteBaseline(id));
  }

  getGoalsStats() {
    return requestLoading(this.materialityRepo.getGoalsStats());
  }

  getGoalStatsByTopic(topicKey: string) {
    return requestLoading(this.materialityRepo.getGoalStatsByTopic(topicKey));
  }

  getGoalList(queryParams: FilterGoalsDto) {
    return requestLoading(this.materialityRepo.getGoalList(queryParams));
  }

  getGoalReviewList(queryParams: FilterGoalsDto) {
    return requestLoading(this.materialityRepo.getGoalReviewList(queryParams));
  }

  sendGoalForReview(goalId: string) {
    return requestLoading(this.materialityRepo.sendGoalForReview(goalId));
  }

  checkGoalForActivate(goalId: string) {
    return requestLoading(this.materialityRepo.checkGoalForActivate(goalId));
  }

  finalizeGoal(goalId: string) {
    return requestLoading(this.materialityRepo.finalizeGoal(goalId));
  }

  autoAllocate(parentGoalId: string, payload: AutoAllocateDto) {
    return requestLoading(
      this.materialityRepo.autoAllocate(parentGoalId, payload),
    );
  }

  reAutoAllocate(parentGoalId: string, payload: AutoAllocateDto) {
    return requestLoading(
      this.materialityRepo.reAutoAllocate(parentGoalId, payload),
    );
  }

  applyAllocation(parentGoalId: string, payload: any) {
    return requestLoading(
      this.materialityRepo.applyAllocation(parentGoalId, payload),
    );
  }

  getFacilitiesAllocate(goalId: string) {
    return requestLoading(this.materialityRepo.getFacilitiesAllocate(goalId));
  }

  getGoalById(id: string) {
    return requestLoading(this.materialityRepo.getGoalById(id));
  }

  createGoal(payload: CreateGoalDto) {
    return requestLoading(this.materialityRepo.createGoal(payload));
  }

  allocateCustomGoal(payload: CustomAllocationDto) {
    return requestLoading(this.materialityRepo.allocateCustomGoal(payload));
  }

  editAllocateCustomGoal(id: string, parentId: string, payload: any) {
    return requestLoading(
      this.materialityRepo.editAllocateCustomGoal(id, parentId, payload),
    );
  }

  updateGoal(id: string, payload: UpdateGoalDto) {
    return requestLoading(this.materialityRepo.updateGoal(id, payload));
  }

  confirmGoal(id: string, payload: ConfirmGoalDto) {
    return requestLoading(this.materialityRepo.confirmGoal(id, payload));
  }

  deleteGoal(id: string) {
    return requestLoading(this.materialityRepo.deleteGoal(id));
  }

  deleteAutoAllocations(id: string) {
    return requestLoading(this.materialityRepo.deleteAutoAllocations(id));
  }

  deleteCustomAllocations(id: string) {
    return requestLoading(this.materialityRepo.deleteCustomAllocations(id));
  }

  getGoalOwners() {
    return requestLoading(this.materialityRepo.getGoalOwners());
  }

  getGoalApprovers() {
    return requestLoading(this.materialityRepo.getGoalApprovers());
  }
}
